/**
 * CaveSystem.js
 *
 * June 2026 — Underground Caves.
 *
 * Two responsibilities:
 *   1) SURFACE — scatter a few glowing cave ENTRANCES on the body surface
 *      (rocky bodies only). The player walks up and presses E to descend.
 *   2) INTERIOR — a self-contained, procedurally generated cavern built at a
 *      far world offset (so it never overlaps the surface terrain). The cave
 *      is intentionally DARK (forcing flashlight use) with a few bioluminescent
 *      crystal glow accents, stalactites/stalagmites, rock pillars, rich
 *      mineable ore veins, hostile creatures, and a glowing exit pad back up.
 *
 * The cave floor/ceiling are pure height functions so GameScene can keep the
 * player snapped to the floor with the same walking code it uses on the
 * surface (it just swaps the ground-height source while `inCave`).
 *
 * Public API:
 *   setBody(body)             // (re)scatter surface entrances
 *   getNearEntrance(radius)   // -> { id, distance } | null
 *   getEntrancePois()         // radar POIs for surface entrances
 *   buildInterior(seed)       // construct the cavern meshes (call on enter)
 *   clearInterior()           // dispose the cavern meshes (call on exit)
 *   showInterior(visible)     // toggle visibility of the cavern root
 *   floorHeight(x,z)          // cavern floor height at world x,z
 *   clamp(x,z)                // keep the player inside the cavern walls
 *   getEntryPoint()           // Vector3 spawn position inside the cave
 *   getExitPos()              // Vector3 of the exit pad
 *   getNearExit(radius)       // -> { distance } | null
 *   getVeins()                // [{ id, pos, radius, mesh }] for MiningSystem
 *   getCreatureSpawns()       // [Vector3] cave-floor monster spawn points
 *   tick(dt)                  // pulse crystal glow (only while inCave)
 *   dispose()
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
  Mesh,
} from '@babylonjs/core';

// Cavern lives far from the surface so the two never share space.
const CX = 5000;
const CZ = 5000;
const R = 56;                 // cavern radius (player stays inside)
const ENTRANCE_RING_MIN = 55;
const ENTRANCE_RING_MAX = 230;
const ENTER_RADIUS = 6.0;
const EXIT_RADIUS = 6.5;

const CRYSTAL_COLORS = [
  new Color3(0.35, 0.85, 1.0),   // cyan
  new Color3(0.7, 0.4, 1.0),     // violet
  new Color3(0.3, 1.0, 0.7),     // teal-green
  new Color3(1.0, 0.55, 0.85),   // pink
];

function hashStr(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xc0ffee;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export default class CaveSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this.glow = opts.glow || null;

    this.CX = CX;
    this.CZ = CZ;
    this.R = R;

    this._surfaceRoot = new TransformNode('caveEntrancesRoot', scene);
    this._caveRoot = null;       // built lazily in buildInterior
    this._entrances = [];        // [{ id, pos:Vector3, mesh }]
    this._veins = [];            // [{ id, pos, radius, mesh }]
    this._creatureSpawns = [];   // [Vector3]
    this._crystalLights = [];    // [{ light, base }]
    this._caveSeed = 1337;
    this._t = 0;
    this._exitPos = new Vector3(CX, 0, CZ);
    this._entryPos = new Vector3(CX, 0, CZ);
  }

  // ---------- deterministic value noise (cavern relief) ----------

  _hash2(x, y, seed) {
    let h = (x * 374761393 + y * 668265263 + seed) | 0;
    h = (h ^ (h >>> 13)) * 1274126177;
    h = h ^ (h >>> 16);
    return ((h >>> 0) % 100000) / 100000;
  }

  _noise(x, y, seed) {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const r00 = this._hash2(xi, yi, seed);
    const r10 = this._hash2(xi + 1, yi, seed);
    const r01 = this._hash2(xi, yi + 1, seed);
    const r11 = this._hash2(xi + 1, yi + 1, seed);
    const sx = xf * xf * (3 - 2 * xf);
    const sy = yf * yf * (3 - 2 * yf);
    const a = r00 * (1 - sx) + r10 * sx;
    const b = r01 * (1 - sx) + r11 * sx;
    return a * (1 - sy) + b * sy;
  }

  _smooth(e0, e1, x) {
    const t = Math.max(0, Math.min(1, (x - e0) / Math.max(1e-6, e1 - e0)));
    return t * t * (3 - 2 * t);
  }

  // ---------- height functions ----------

  floorHeight(x, z) {
    const lx = x - CX;
    const lz = z - CZ;
    const r = Math.hypot(lx, lz);
    let h = (this._noise(lx * 0.06 + 3.1, lz * 0.06 - 1.7, this._caveSeed) - 0.5) * 4.4;
    h += (this._noise(lx * 0.16 + 9.0, lz * 0.16 + 4.0, this._caveSeed + 7) - 0.5) * 1.6;
    // Raise the floor near the walls so the cavern reads as a bowl.
    h += this._smooth(R - 16, R, r) * 5.0;
    return h;
  }

  ceilHeight(x, z) {
    const lx = x - CX;
    const lz = z - CZ;
    const r = Math.hypot(lx, lz);
    let c = 16 + (this._noise(lx * 0.05 + 2.0, lz * 0.05 + 6.0, this._caveSeed + 22) - 0.5) * 9;
    // Ceiling dips toward the walls (closes the cavern off).
    c -= this._smooth(R - 18, R, r) * 8.0;
    return c;
  }

  clamp(x, z) {
    const lx = x - CX;
    const lz = z - CZ;
    const r = Math.hypot(lx, lz);
    const limit = R - 2.5;
    if (r <= limit) return { x, z };
    const k = limit / r;
    return { x: CX + lx * k, z: CZ + lz * k };
  }

  // ---------- surface entrances ----------

  setBody(body) {
    this.clearSurface();
    if (!body) return;
    // Rocky bodies only — no entrances on Gas Giants (no real surface).
    if (body.type === 'Gas Giant') return;

    const key = `${body.coords || 'k'}:${body.currentPlanetIdx ?? 0}:${body.currentMoonIdx ?? 'x'}`;
    this._bodyKey = key;
    const seed = hashStr(`cave|${body.name || ''}|${body.type || ''}|${body.currentPlanetIdx ?? 0}`);
    const rng = mkRng(seed);
    const count = 2 + Math.floor(rng() * 3); // 2..4 entrances
    for (let i = 0; i < count; i++) {
      const ang = rng() * Math.PI * 2;
      const rad = ENTRANCE_RING_MIN + rng() * (ENTRANCE_RING_MAX - ENTRANCE_RING_MIN);
      const x = Math.cos(ang) * rad;
      const z = Math.sin(ang) * rad;
      const y = this.getTerrainHeight(x, z);
      const id = `${key}#cave${i}`;
      const mesh = this._buildEntranceMesh(id, i, rng);
      mesh.position.set(x, y, z);
      this._entrances.push({ id, pos: new Vector3(x, y, z), mesh });
    }
  }

  _buildEntranceMesh(id, idx, rng) {
    const root = new TransformNode(`caveEntrance_${idx}`, this.scene);
    root.parent = this._surfaceRoot;

    // Dark mouth — a black disc set just into the ground.
    const mouth = MeshBuilder.CreateDisc(`caveMouth_${idx}`, { radius: 3.6, tessellation: 28 }, this.scene);
    mouth.rotation.x = Math.PI / 2;
    mouth.position.y = 0.15;
    mouth.parent = root;
    const mouthMat = new StandardMaterial(`caveMouthMat_${idx}`, this.scene);
    mouthMat.diffuseColor = new Color3(0, 0, 0);
    mouthMat.emissiveColor = new Color3(0.01, 0.01, 0.02);
    mouthMat.specularColor = new Color3(0, 0, 0);
    mouthMat.disableLighting = true;
    mouth.material = mouthMat;
    mouth.isPickable = false;

    // Jagged rock rim around the mouth.
    const rimCount = 9;
    for (let r = 0; r < rimCount; r++) {
      const a = (r / rimCount) * Math.PI * 2;
      const rock = MeshBuilder.CreatePolyhedron(`caveRim_${idx}_${r}`, { type: 1, size: 1.0 + rng() * 0.9 }, this.scene);
      rock.position.set(Math.cos(a) * 4.0, 0.5 + rng() * 0.4, Math.sin(a) * 4.0);
      rock.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
      rock.scaling.y = 1.2 + rng() * 0.8;
      rock.parent = root;
      const rm = new StandardMaterial(`caveRimMat_${idx}_${r}`, this.scene);
      rm.diffuseColor = new Color3(0.18, 0.17, 0.2);
      rm.specularColor = new Color3(0.03, 0.03, 0.03);
      rock.material = rm;
      rock.isPickable = false;
    }

    // Glowing crystal cluster marking the entrance (so it reads at a distance).
    const col = CRYSTAL_COLORS[idx % CRYSTAL_COLORS.length];
    for (let c = 0; c < 4; c++) {
      const a = rng() * Math.PI * 2;
      const cr = MeshBuilder.CreateCylinder(`caveBeacon_${idx}_${c}`, {
        diameterTop: 0, diameterBottom: 0.5 + rng() * 0.3, height: 1.6 + rng() * 1.2, tessellation: 5,
      }, this.scene);
      cr.position.set(Math.cos(a) * (3.4 + rng()), 0.7, Math.sin(a) * (3.4 + rng()));
      cr.rotation.z = (rng() - 0.5) * 0.5;
      cr.parent = root;
      const cm = new StandardMaterial(`caveBeaconMat_${idx}_${c}`, this.scene);
      cm.diffuseColor = col.scale(0.3);
      cm.emissiveColor = col.scale(1.2);
      cm.specularColor = new Color3(0, 0, 0);
      cr.material = cm;
      cr.isPickable = false;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(cr); } catch (_e) { /* noop */ } }
    }

    // Soft beacon light at the mouth.
    const light = new PointLight(`caveBeaconLight_${idx}`, new Vector3(0, 2.0, 0), this.scene);
    light.diffuse = col;
    light.intensity = 0.55;
    light.range = 18;
    light.parent = root;

    root.metadata = { caveId: id };
    return root;
  }

  getNearEntrance(radius = ENTER_RADIUS) {
    if (this._entrances.length === 0) return null;
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = radius;
    for (const e of this._entrances) {
      const d = Math.hypot(p.x - e.pos.x, p.z - e.pos.z);
      if (d < bestDist) {
        bestDist = d;
        best = { id: e.id, distance: d };
      }
    }
    return best;
  }

  getEntrancePois() {
    return this._entrances.map((e) => ({ id: e.id, kind: 'cave', x: e.pos.x, z: e.pos.z }));
  }

  clearSurface() {
    for (const e of this._entrances) {
      try { e.mesh.dispose(false, true); } catch (_e) { /* noop */ }
    }
    this._entrances = [];
  }

  // ---------- cavern interior ----------

  seedForEntrance(id) {
    return (hashStr(id || 'cave') ^ 0x5caf3d) >>> 0;
  }

  buildInterior(seed) {
    this.clearInterior();
    this._caveSeed = (seed | 0) >>> 0 || 1337;
    const rng = mkRng(this._caveSeed);
    const root = new TransformNode('caveInteriorRoot', this.scene);
    this._caveRoot = root;
    this._veins = [];
    this._creatureSpawns = [];
    this._crystalLights = [];

    const rockMat = new StandardMaterial('caveRockMat', this.scene);
    rockMat.diffuseColor = new Color3(0.32, 0.3, 0.34);
    rockMat.specularColor = new Color3(0.04, 0.04, 0.05);
    rockMat.maxSimultaneousLights = 8;

    const darkRockMat = new StandardMaterial('caveDarkRockMat', this.scene);
    darkRockMat.diffuseColor = new Color3(0.2, 0.19, 0.23);
    darkRockMat.specularColor = new Color3(0.02, 0.02, 0.03);
    darkRockMat.maxSimultaneousLights = 8;

    // --- Floor (deformed ground) ---
    const span = R * 2 + 20;
    const sub = 64;
    const floor = MeshBuilder.CreateGround('caveFloor', { width: span, height: span, subdivisions: sub, updatable: true }, this.scene);
    floor.parent = root;
    floor.position.set(CX, 0, CZ);
    this._deform(floor, span, sub, (wx, wz) => this.floorHeight(wx, wz));
    floor.material = rockMat;
    floor.isPickable = false;
    floor.receiveShadows = true;

    // --- Ceiling (deformed ground, double-sided so we see the underside) ---
    const ceil = MeshBuilder.CreateGround('caveCeiling', { width: span, height: span, subdivisions: 32, updatable: true }, this.scene);
    ceil.parent = root;
    ceil.position.set(CX, 0, CZ);
    this._deform(ceil, span, 32, (wx, wz) => this.ceilHeight(wx, wz));
    const ceilMat = new StandardMaterial('caveCeilMat', this.scene);
    ceilMat.diffuseColor = new Color3(0.15, 0.14, 0.17);
    ceilMat.specularColor = new Color3(0, 0, 0);
    ceilMat.backFaceCulling = false;
    ceilMat.maxSimultaneousLights = 8;
    ceil.material = ceilMat;
    ceil.isPickable = false;

    // --- Perimeter wall (open cylinder, inner faces visible) ---
    const wall = MeshBuilder.CreateCylinder('caveWall', {
      diameter: R * 2, height: 60, tessellation: 56, cap: Mesh.NO_CAP,
    }, this.scene);
    wall.parent = root;
    wall.position.set(CX, 14, CZ);
    const wallMat = new StandardMaterial('caveWallMat', this.scene);
    wallMat.diffuseColor = new Color3(0.22, 0.2, 0.24);
    wallMat.specularColor = new Color3(0.02, 0.02, 0.03);
    wallMat.backFaceCulling = false;
    wallMat.maxSimultaneousLights = 8;
    wall.material = wallMat;
    wall.isPickable = false;

    // --- Rock pillars (floor → ceiling columns) ---
    const pillars = 5 + Math.floor(rng() * 4);
    for (let i = 0; i < pillars; i++) {
      const a = rng() * Math.PI * 2;
      const rad = 12 + rng() * (R - 22);
      const lx = Math.cos(a) * rad;
      const lz = Math.sin(a) * rad;
      const wx = CX + lx;
      const wz = CZ + lz;
      const fy = this.floorHeight(wx, wz);
      const cy = this.ceilHeight(wx, wz);
      const height = Math.max(6, cy - fy + 2);
      const dia = 2.2 + rng() * 3.5;
      const col = MeshBuilder.CreateCylinder(`cavePillar_${i}`, {
        diameterTop: dia * (0.6 + rng() * 0.5), diameterBottom: dia, height, tessellation: 7,
      }, this.scene);
      col.position.set(wx, fy + height / 2 - 1, wz);
      col.rotation.y = rng() * Math.PI;
      col.parent = root;
      col.material = darkRockMat;
      col.isPickable = false;
    }

    // --- Stalagmites (up from floor) + Stalactites (down from ceiling) ---
    const spikes = 30;
    for (let i = 0; i < spikes; i++) {
      const a = rng() * Math.PI * 2;
      const rad = rng() * (R - 6);
      const lx = Math.cos(a) * rad;
      const lz = Math.sin(a) * rad;
      const wx = CX + lx;
      const wz = CZ + lz;
      const up = rng() > 0.45;
      const len = 1.6 + rng() * 4.5;
      const dia = 0.6 + rng() * 1.6;
      if (up) {
        const fy = this.floorHeight(wx, wz);
        const m = MeshBuilder.CreateCylinder(`caveStalagmite_${i}`, { diameterTop: 0, diameterBottom: dia, height: len, tessellation: 6 }, this.scene);
        m.position.set(wx, fy + len / 2, wz);
        m.parent = root;
        m.material = rockMat;
        m.isPickable = false;
      } else {
        const cy = this.ceilHeight(wx, wz);
        const m = MeshBuilder.CreateCylinder(`caveStalactite_${i}`, { diameterTop: dia, diameterBottom: 0, height: len, tessellation: 6 }, this.scene);
        m.position.set(wx, cy - len / 2, wz);
        m.parent = root;
        m.material = darkRockMat;
        m.isPickable = false;
      }
    }

    // --- Glowing crystal clusters (the few light accents) ---
    const clusters = 5;
    for (let i = 0; i < clusters; i++) {
      const a = (i / clusters) * Math.PI * 2 + rng() * 0.6;
      const rad = 14 + rng() * (R - 24);
      const lx = Math.cos(a) * rad;
      const lz = Math.sin(a) * rad;
      const wx = CX + lx;
      const wz = CZ + lz;
      const fy = this.floorHeight(wx, wz);
      const col = CRYSTAL_COLORS[i % CRYSTAL_COLORS.length];
      const cm = new StandardMaterial(`caveCrystalMat_${i}`, this.scene);
      cm.diffuseColor = col.scale(0.25);
      cm.emissiveColor = col.scale(1.3);
      cm.specularColor = new Color3(0, 0, 0);
      const shards = 3 + Math.floor(rng() * 4);
      for (let s = 0; s < shards; s++) {
        const sh = MeshBuilder.CreateCylinder(`caveCrystal_${i}_${s}`, {
          diameterTop: 0, diameterBottom: 0.3 + rng() * 0.4, height: 1.4 + rng() * 2.4, tessellation: 5,
        }, this.scene);
        sh.position.set(wx + (rng() - 0.5) * 2.2, fy + 0.6 + rng() * 0.4, wz + (rng() - 0.5) * 2.2);
        sh.rotation.set((rng() - 0.5) * 0.6, rng() * Math.PI, (rng() - 0.5) * 0.6);
        sh.parent = root;
        sh.material = cm;
        sh.isPickable = false;
        if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(sh); } catch (_e) { /* noop */ } }
      }
      const light = new PointLight(`caveCrystalLight_${i}`, new Vector3(wx, fy + 2.2, wz), this.scene);
      light.diffuse = col;
      light.intensity = 0.7;
      light.range = 24;
      light.parent = root;
      this._crystalLights.push({ light, base: 0.7, phase: rng() * Math.PI * 2 });
    }

    // --- Rich mineable ore veins (registered with MiningSystem) ---
    const veinCount = 6 + Math.floor(rng() * 3);
    for (let i = 0; i < veinCount; i++) {
      const a = rng() * Math.PI * 2;
      const rad = 10 + rng() * (R - 18);
      const lx = Math.cos(a) * rad;
      const lz = Math.sin(a) * rad;
      const wx = CX + lx;
      const wz = CZ + lz;
      const fy = this.floorHeight(wx, wz);
      const size = 2.8 + rng() * 4.2;
      const rock = MeshBuilder.CreatePolyhedron(`caveVein_${i}`, { type: Math.floor(rng() * 4), size }, this.scene);
      rock.position.set(wx, fy + size * 0.4, wz);
      rock.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
      rock.parent = root;
      rock.material = darkRockMat;
      rock.isPickable = false;
      this._veins.push({ id: `cavevein_${this._caveSeed}_${i}`, pos: rock.position.clone(), radius: size, mesh: rock });
    }

    // --- Creature spawn points (deeper half of the cavern) ---
    const monsters = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < monsters; i++) {
      const a = rng() * Math.PI * 2;
      const rad = 18 + rng() * (R - 26);
      const wx = CX + Math.cos(a) * rad;
      const wz = CZ + Math.sin(a) * rad;
      this._creatureSpawns.push(new Vector3(wx, this.floorHeight(wx, wz), wz));
    }

    // --- Entry / exit pad (near the wall, away from monsters) ---
    const entryLx = 0;
    const entryLz = R - 13;
    const ex = CX + entryLx;
    const ez = CZ + entryLz;
    const efy = this.floorHeight(ex, ez);
    this._entryPos = new Vector3(ex, efy, ez);
    this._exitPos = new Vector3(ex, efy, ez);

    const pad = MeshBuilder.CreateTorus('caveExitPad', { diameter: 5, thickness: 0.4, tessellation: 28 }, this.scene);
    pad.position.set(ex, efy + 0.2, ez);
    pad.parent = root;
    const padMat = new StandardMaterial('caveExitPadMat', this.scene);
    padMat.diffuseColor = new Color3(0.1, 0.3, 0.15);
    padMat.emissiveColor = new Color3(0.2, 1.0, 0.45);
    padMat.specularColor = new Color3(0, 0, 0);
    pad.material = padMat;
    pad.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(pad); } catch (_e) { /* noop */ } }

    // Upward light beam at the exit so the way out is always findable.
    const beam = MeshBuilder.CreateCylinder('caveExitBeam', { diameterTop: 0.4, diameterBottom: 3.2, height: 30, tessellation: 18 }, this.scene);
    beam.position.set(ex, efy + 15, ez);
    beam.parent = root;
    const beamMat = new StandardMaterial('caveExitBeamMat', this.scene);
    beamMat.emissiveColor = new Color3(0.2, 0.9, 0.5);
    beamMat.diffuseColor = new Color3(0, 0, 0);
    beamMat.specularColor = new Color3(0, 0, 0);
    beamMat.disableLighting = true;
    beamMat.alpha = 0.12;
    beamMat.backFaceCulling = false;
    beam.material = beamMat;
    beam.isPickable = false;

    const exitLight = new PointLight('caveExitLight', new Vector3(ex, efy + 2.5, ez), this.scene);
    exitLight.diffuse = new Color3(0.3, 1.0, 0.5);
    exitLight.intensity = 0.6;
    exitLight.range = 20;
    exitLight.parent = root;

    return root;
  }

  _deform(mesh, span, sub, heightFn) {
    const positions = mesh.getVerticesData('position');
    if (!positions) return;
    const cx = mesh.position.x;
    const cz = mesh.position.z;
    for (let i = 0; i < positions.length; i += 3) {
      const wx = cx + positions[i];
      const wz = cz + positions[i + 2];
      positions[i + 1] = heightFn(wx, wz) - mesh.position.y;
    }
    mesh.updateVerticesData('position', positions);
    try { mesh.createNormals(true); } catch (_e) { /* noop */ }
  }

  showInterior(visible) {
    if (this._caveRoot) this._caveRoot.setEnabled(!!visible);
  }

  clearInterior() {
    if (this._caveRoot) {
      try { this._caveRoot.dispose(false, true); } catch (_e) { /* noop */ }
      this._caveRoot = null;
    }
    this._veins = [];
    this._creatureSpawns = [];
    this._crystalLights = [];
  }

  getEntryPoint() { return this._entryPos.clone(); }
  getExitPos() { return this._exitPos.clone(); }

  getNearExit(radius = EXIT_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._exitPos.x, p.z - this._exitPos.z);
    return d < radius ? { distance: d } : null;
  }

  getVeins() { return this._veins.map((v) => ({ ...v })); }
  getCreatureSpawns() { return this._creatureSpawns.map((s) => s.clone()); }

  tick(dt) {
    this._t += dt;
    for (let i = 0; i < this._crystalLights.length; i++) {
      const c = this._crystalLights[i];
      if (!c.light) continue;
      c.light.intensity = c.base * (0.7 + 0.3 * Math.sin(this._t * 1.4 + c.phase));
    }
  }

  dispose() {
    this.clearSurface();
    this.clearInterior();
    try { this._surfaceRoot.dispose(); } catch (_e) { /* noop */ }
  }
}
